import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography,Chip, Checkbox, Table, TableHead, TableBody, TableRow, TableCell, Avatar, TableContainer, Paper, Box
} from '@mui/material';
import ConfirmationDialog from './ConfirmationDialog.comp';

const LinkProgramDialog = ({programs, open, onSubmit, loading, onClose, orgName, orgCode, linkedProgramCodes = [], linkingLoading = false,}) => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
    const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedCodes([]);
            setShowFirstConfirmation(false);
            setShowSecondConfirmation(false);
        }
    }, [open]);

    const handleToggle = (code) => {
        setSelectedCodes(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };

    const handleSubmit = () => {
        // Show first confirmation
        setShowFirstConfirmation(true);
    };

    const handleFirstConfirm = () => {
        setShowFirstConfirmation(false);
        // Show second confirmation
        setShowSecondConfirmation(true);
    };

    const handleSecondConfirm = () => {
        setShowSecondConfirmation(false);
        // Actually call the API
        onSubmit(orgCode, selectedCodes);
    };

    const handleCloseFirstConfirmation = () => {
        setShowFirstConfirmation(false);
    };

    const handleCloseSecondConfirmation = () => {
        setShowSecondConfirmation(false);
    };

    const linked = programs.filter(a => linkedProgramCodes.includes(a.code));
    const unlinked = programs.filter(a => !linkedProgramCodes.includes(a.code));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ color: '#ff6468', fontWeight: 'bold', textAlign: 'center' }}>
                Link Programs to {orgName}
            </DialogTitle>
            <DialogContent>
                {linkingLoading  ? (
                    <Typography>Loading programs...</Typography>
                ) : (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '17px', mb: 2 }}>
                            Select the program(s) that you would like to link to this client.
                        </Typography>
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Program Name</TableCell>
                                        <TableCell>Questions</TableCell>
                                        <TableCell>Time (min)</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...linked, ...unlinked].map((program) => {
                                        const alreadyLinked = linkedProgramCodes.includes(program.code);
                                        const notReady = !program.is_ready;

                                        return (
                                            <TableRow
                                                key={program.code || program.id}
                                                sx={{
                                                    backgroundColor: alreadyLinked ? '#fbe9e7' : notReady ? '#f0f0f0' : 'inherit'
                                                }}
                                            >
                                                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        src={program.image_url}
                                                        variant="rounded"
                                                        sx={{ width: 56, height: 56, border: '1.5px solid #ff6468' }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                lineHeight: '1.5',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                marginLeft:'15px',
                                                            }}
                                                        >
                                                            {program.full_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{program.length_questions}</TableCell>
                                                <TableCell>{program.time_to_complete_minutes}</TableCell>
                                                <TableCell align="center">
                                                    {alreadyLinked ? (
                                                        <Chip
                                                            label="Already Linked"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:'transparent',
                                                                color: '#0097a7',
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    ) : notReady ? (
                                                        <Chip
                                                            label="Not Ready"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:'transparent',
                                                                color: '#D32F2F',
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            checked={selectedCodes.includes(program.code)}
                                                            onChange={() => handleToggle(program.code)}
                                                            sx={{
                                                                color: '#ff6468',
                                                                '&.Mui-checked': { color: '#ff6468' }
                                                            }}
                                                            disabled={alreadyLinked || notReady}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Box sx={{ display: 'flex', width: '100%',alignItems:'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '20px',
                        minWidth: '120px',
                        fontWeight: 'bold',
                        borderColor: '#0097b2',
                        color: '#0097b2',
                    }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading || selectedCodes.length === 0}
                        sx={{ backgroundColor: '#ff6468', borderRadius: '20px', minWidth: 120 }}
                    >
                        {loading ? 'Linking...' : 'Submit'}
                    </Button>
                </Box>
            </DialogActions>

            {/* First Confirmation Dialog */}
            <ConfirmationDialog
                open={showFirstConfirmation}
                onClose={handleCloseFirstConfirmation}
                onConfirm={handleFirstConfirm}
                title="Confirm Linking"
                message={`Do you really want to link <b>${selectedCodes.length}</b> program(s) to <b>${orgName}?</b>`}
                confirmText="Proceed"
                cancelText="Cancel"
            />

            {/* Second Confirmation Dialog */}
            <ConfirmationDialog
                open={showSecondConfirmation}
                onClose={handleCloseSecondConfirmation}
                onConfirm={handleSecondConfirm}
                title="Are You Sure?"
                message="Are you sure? Unlinking will not be possible once linked."
                confirmText="Yes, Link Now"
                cancelText="Cancel"
                isWarning={true}
            />
        </Dialog>
    );
};

export default LinkProgramDialog;